/**
 * W3: Sync plan prices, ARPU, COGS from Economics module localStorage.
 */

import {
  SCENARIO_STORAGE_KEY as ECONOMICS_KEY,
  LEGACY_SCENARIO_STORAGE_KEY as ECONOMICS_LEGACY_KEY,
  LEGACY_SCENARIO_STORAGE_KEY_V2 as ECONOMICS_LEGACY_KEY_V2,
  LEGACY_SCENARIO_STORAGE_KEY_V1 as ECONOMICS_LEGACY_KEY_V1,
} from '../../economics/model/seedDefaults';
import { profilePnL } from '../../economics/model/engine';

function loadEconomicsScenario() {
  for (const key of [
    ECONOMICS_KEY,
    ECONOMICS_LEGACY_KEY,
    ECONOMICS_LEGACY_KEY_V2,
    ECONOMICS_LEGACY_KEY_V1,
  ]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.plans && parsed?.profiles) return parsed;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function weightedPaidGrowthRows(economicsScenario) {
  const { plans, profiles, growth } = economicsScenario;
  const profileById = new Map((profiles || []).map((profile) => [profile.id, profile]));
  const rows = (growth?.profileGrowth || []).filter((row) => profileById.has(row.profileId));
  const paidRows = rows.filter((row) => {
    const profile = profileById.get(row.profileId);
    return Math.max(0, Number(plans?.[profile.plan]?.monthly) || 0) > 0;
  });
  const totalPaidShare = paidRows.reduce((sum, row) => sum + safeShare(row.share0), 0);

  // Forecast represents paying logos. Never fall back to Free rows (or to the
  // first Basic profile) when the Economics mix explicitly contains no paid
  // share; that silently invents revenue during a sync.
  if (paidRows.length === 0 || totalPaidShare <= 0) return [];

  const denominator = totalPaidShare;
  return paidRows.map((row) => ({
    ...row,
    normalizedShare: safeShare(row.share0) / denominator,
  }));
}

function safeShare(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

/**
 * Weighted ARPU from economics profiles + profileGrowth shares.
 * @param {object} economicsScenario
 */
export function extractWeightedArpu(economicsScenario) {
  const { plans, profiles } = economicsScenario;
  const growthRows = weightedPaidGrowthRows(economicsScenario);

  let weighted = 0;
  for (const row of growthRows) {
    const profile = profiles.find((p) => p.id === row.profileId);
    if (!profile) continue;
    const share = row.normalizedShare;
    const plan = plans[profile.plan] || { monthly: 0 };
    weighted += share * Math.max(0, Number(plan.monthly) || 0);
  }

  return Math.round(weighted * 100) / 100;
}

function weightedPnlField(economicsScenario, field) {
  const { plans, profiles, rates } = economicsScenario;
  const growthRows = weightedPaidGrowthRows(economicsScenario);

  let weighted = 0;
  for (const row of growthRows) {
    const profile = profiles.find((p) => p.id === row.profileId);
    if (!profile) continue;
    const share = row.normalizedShare;
    const pnl = profilePnL(profile, plans, rates);
    weighted += share * (pnl[field] || 0);
  }

  return Math.round(weighted * 100) / 100;
}

/**
 * Average Plaid+Stripe COGS per paying user (excludes AI).
 * @param {object} economicsScenario
 */
export function extractAvgCogsPerLogo(economicsScenario) {
  return Math.round(
    (extractAvgPlaidCogsPerLinkedLogo(economicsScenario) +
      extractAvgStripeCogsPerPaidLogo(economicsScenario)) *
      100
  ) / 100;
}

/**
 * Plaid per linked logo + implied paid→bank-link rate from Economics mix.
 * Paying-weighted Plaid divided by linked share — not by all paying logos.
 * @param {object} economicsScenario
 */
function extractPlaidLinkStats(economicsScenario) {
  const { plans, profiles, rates } = economicsScenario;
  const growthRows = weightedPaidGrowthRows(economicsScenario);
  if (growthRows.length === 0) {
    return {
      plaidPerLinked: 0,
      impliedPaidToBankLinkPct: 0,
      hasPaidMix: false,
      hasLinkedPaying: false,
    };
  }

  let linkedShare = 0;
  let weightedCogs = 0;
  for (const row of growthRows) {
    const profile = profiles.find((p) => p.id === row.profileId);
    if (!profile) continue;
    const share = row.normalizedShare;
    const pnl = profilePnL(profile, plans, rates);
    weightedCogs += share * (pnl.cogs || 0);
    if ((pnl.linkedItems || 0) > 0) linkedShare += share;
  }

  const plaidPerLinked = linkedShare > 0 ? weightedCogs / linkedShare : 0;
  return {
    plaidPerLinked,
    impliedPaidToBankLinkPct: Math.round(linkedShare * 10000) / 100,
    hasPaidMix: true,
    hasLinkedPaying: linkedShare > 0,
  };
}

/**
 * Average Plaid COGS per linked paid logo (not per paying logo).
 * @param {object} economicsScenario
 */
export function extractAvgPlaidCogsPerLinkedLogo(economicsScenario) {
  return extractPlaidLinkStats(economicsScenario).plaidPerLinked;
}

/**
 * Economics implied paid→bank-link % (linked paid share / total paid share).
 * 0 when there is no paid mix or no linked paying profiles — never invents 50.
 * @param {object} economicsScenario
 */
export function extractImpliedPaidToBankLinkPct(economicsScenario) {
  return extractPlaidLinkStats(economicsScenario).impliedPaidToBankLinkPct;
}

/**
 * Average Stripe COGS per paid logo.
 * @param {object} economicsScenario
 */
export function extractAvgStripeCogsPerPaidLogo(economicsScenario) {
  return weightedPnlField(economicsScenario, 'stripe');
}

/**
 * Average AI COGS per paying logo (Premium+ only; Basic/Free = 0).
 * @param {object} economicsScenario
 */
export function extractAvgAiCogsPerLogo(economicsScenario) {
  return weightedPnlField(economicsScenario, 'ai');
}

/**
 * Average AI pack revenue per paying logo (overage packs only).
 * @param {object} economicsScenario
 */
export function extractAvgAiPackRevenuePerLogo(economicsScenario) {
  return weightedPnlField(economicsScenario, 'aiPackRevenue');
}

const CATALOG_MONTHLY = { basic: 9.99, premium: 24.99, enterprise: 99.99 };

function resolvePlanMonthly(plan, catalogFallback) {
  if (Number.isFinite(plan?.monthly)) return plan.monthly;
  return catalogFallback;
}

/**
 * Plan prices and tier mix from economics.
 * @param {object} economicsScenario
 */
export function extractPlanPricing(economicsScenario) {
  const { plans, profiles } = economicsScenario;
  const growthRows = weightedPaidGrowthRows(economicsScenario);
  const profileById = new Map((profiles || []).map((profile) => [profile.id, profile]));
  const rawMix = { basic: 0, premium: 0, enterprise: 0 };
  for (const row of growthRows) {
    const profile = profileById.get(row.profileId);
    const tier = profile?.plan;
    if (!(tier in rawMix)) continue;
    rawMix[tier] += row.normalizedShare;
  }
  const planMix = rawMix;
  if (growthRows.length === 0) {
    return {
      arpuByTier: {
        ...(plans.free != null ? { free: plans.free.monthly ?? 0 } : {}),
      },
      planMix,
    };
  }
  return {
    arpuByTier: {
      basic: resolvePlanMonthly(plans.basic, CATALOG_MONTHLY.basic),
      premium: resolvePlanMonthly(plans.premium, CATALOG_MONTHLY.premium),
      enterprise: resolvePlanMonthly(plans.enterprise, CATALOG_MONTHLY.enterprise),
      free: Number.isFinite(plans.free?.monthly) ? plans.free.monthly : 0,
    },
    planMix,
  };
}

/**
 * Pull economics data into forecast pack assumptions.
 * @param {object} forecastScenario
 * @param {object} [economicsScenario]
 */
export function pullFromEconomics(forecastScenario, economicsScenario = loadEconomicsScenario()) {
  if (!economicsScenario) {
    return { scenario: forecastScenario, synced: false, message: 'No Economics scenario found in localStorage.' };
  }

  const arpu = extractWeightedArpu(economicsScenario);
  const plaidStats = extractPlaidLinkStats(economicsScenario);
  const plaidCogs = plaidStats.plaidPerLinked;
  const stripeCogs = extractAvgStripeCogsPerPaidLogo(economicsScenario);
  const cogs = Math.round((plaidCogs + stripeCogs) * 100) / 100;
  const aiCogs = extractAvgAiCogsPerLogo(economicsScenario);
  const aiPackRevenue = extractAvgAiPackRevenuePerLogo(economicsScenario);
  const pricing = extractPlanPricing(economicsScenario);
  const hasPaidProfiles = plaidStats.hasPaidMix;
  const startPaying = hasPaidProfiles ? economicsScenario.growth?.startPaying ?? 0 : 0;

  const activeKey = forecastScenario.activeScenario || 'base';
  const pack = forecastScenario.packs[activeKey];

  const openingMrr = Math.round(startPaying * arpu * 100) / 100;

  const nextAssumptions = {
    ...pack.assumptions,
    openingLogos: startPaying,
    openingMrr,
    variableCogsPerLogo: cogs,
    plaidCogsPerLinkedLogo: plaidCogs,
    stripeCogsPerPaidLogo: stripeCogs,
    aiCogsPerLogo: aiCogs,
    aiPackRevenuePerLogo: aiPackRevenue,
    planMix: pricing.planMix,
  };
  // Free-only / no paid share: do not overwrite pack list prices with 9.99/24.99/99.99.
  if (hasPaidProfiles) {
    nextAssumptions.arpuByTier = pricing.arpuByTier;
  }

  const nextPack = {
    ...pack,
    assumptions: nextAssumptions,
  };
  if (hasPaidProfiles) {
    nextPack.funnel = {
      ...pack.funnel,
      paidToBankLinkPct: plaidStats.impliedPaidToBankLinkPct,
    };
  }

  const next = {
    ...forecastScenario,
    syncedFromEconomics: {
      at: new Date().toISOString(),
      source: 'economics',
      arpu,
      cogs,
      plaidCogs,
      stripeCogs,
      aiCogs,
      aiPackRevenue,
      startPaying,
    },
    packs: {
      ...forecastScenario.packs,
      [activeKey]: nextPack,
    },
  };

  return {
    scenario: next,
    synced: true,
    message: hasPaidProfiles
      ? `Synced ARPU $${arpu}, Plaid $${plaidCogs}/linked + Stripe $${stripeCogs}/paid COGS, AI COGS $${aiCogs}, AI packs $${aiPackRevenue}/logo, ${startPaying} paying.`
      : 'No paid Economics profile mix found; synced zero paying logos and zero monetized revenue.',
  };
}

/**
 * Push forecast P50 logos at horizon M12 (or last month) to economics startPaying.
 * @param {object} forecastScenario
 * @param {object} projection — from projectForecast
 */
export function pushLogosToEconomics(forecastScenario, projection) {
  if (forecastScenario?.monteCarlo?.enabled && projection?.mc !== true) {
    return {
      ok: false,
      synced: false,
      message: 'Waiting for Monte Carlo P50',
    };
  }

  let economics = null;
  let storageKey = ECONOMICS_KEY;
  for (const key of [
    ECONOMICS_KEY,
    ECONOMICS_LEGACY_KEY,
    ECONOMICS_LEGACY_KEY_V2,
    ECONOMICS_LEGACY_KEY_V1,
  ]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      economics = JSON.parse(raw);
      storageKey = key;
      break;
    } catch {
      /* ignore */
    }
  }

  if (!economics) {
    return { ok: false, synced: false, message: 'No Economics scenario in localStorage.' };
  }

  try {
    const months = projection?.months || [];
    if (months.length === 0) {
      return { ok: false, synced: false, message: 'Forecast projection has no months.' };
    }
    const targetIdx = Math.min(12, months.length - 1);
    const logos = months[targetIdx]?.logosP50 ?? months[targetIdx]?.endLogos ?? 0;

    const next = {
      ...economics,
      growth: {
        ...(economics.growth || {}),
        startPaying: Math.round(logos * 10) / 10,
      },
    };

    // Always promote legacy reads to the canonical key. Otherwise Forecast
    // could update v2 while Economics continues loading a stale v4 scenario.
    localStorage.setItem(ECONOMICS_KEY, JSON.stringify(next));
    if (storageKey !== ECONOMICS_KEY) localStorage.removeItem(storageKey);
    if (storageKey !== ECONOMICS_KEY) {
      localStorage.setItem(ECONOMICS_KEY, JSON.stringify(next));
    }

    return {
      ok: true,
      synced: true,
      message: `Set Economics startPaying to ${logos} (from forecast M${targetIdx}).`,
      logos,
    };
  } catch {
    return { ok: false, synced: false, message: 'Failed to update Economics localStorage.' };
  }
}

export { loadEconomicsScenario, ECONOMICS_KEY, ECONOMICS_LEGACY_KEY };
